export type AttachmentKind = 
  | 'ID_DOC' 
  | 'PHOTO' 
  | 'KNOWLEDGE_TEST' 
  | 'SKILL_TEST' 
  | 'MEDICAL_CERT' 
  | 'FEE_RECEIPT' 
  | 'OTHER';

export interface MockAttachment {
  id: number;
  applicant_id: number;
  kind: AttachmentKind;
  document_type: string;
  url: string;
  file_name: string;
  file_type: string;
  file_size: number;
  status: 'UPLOADED' | 'VERIFIED' | 'REJECTED';
  checksum?: string;
  uploaded_by: number;
  uploaded_at: string;
  notes?: string;
}

export const mockAttachments: MockAttachment[] = [
  {
    id: 1,
    applicant_id: 1,
    kind: 'PHOTO',
    document_type: 'Passport Photograph',
    url: 'https://ui-avatars.com/api/?name=John+Okafor&size=200',
    file_name: 'passport_photo.jpg',
    file_type: 'image/jpeg',
    file_size: 245760,
    status: 'VERIFIED',
    checksum: 'abc123def456',
    uploaded_by: 2,
    uploaded_at: '2024-01-20T14:30:00Z'
  },
  {
    id: 2,
    applicant_id: 1,
    kind: 'ID_DOC',
    document_type: 'NIN Slip',
    url: '/mock-documents/nin_card.pdf',
    file_name: 'nin_card_12345678901.pdf',
    file_type: 'application/pdf',
    file_size: 512000,
    status: 'VERIFIED',
    checksum: 'def456ghi789',
    uploaded_by: 2,
    uploaded_at: '2024-01-20T14:35:00Z'
  },
  {
    id: 3,
    applicant_id: 1,
    kind: 'MEDICAL_CERT',
    document_type: 'Medical Certificate',
    url: '/mock-documents/medical_certificate.pdf',
    file_name: 'medical_cert_class_1.pdf',
    file_type: 'application/pdf',
    file_size: 768000,
    status: 'UPLOADED',
    checksum: 'ghi789jkl012',
    uploaded_by: 2,
    uploaded_at: '2024-02-15T10:20:00Z',
    notes: 'Medical certificate is under review by our medical team.'
  },
  {
    id: 4,
    applicant_id: 1,
    kind: 'SKILL_TEST',
    document_type: 'Training Certificate',
    url: '/mock-documents/skill_test_certificate.pdf',
    file_name: 'atc_skill_test_pass.pdf',
    file_type: 'application/pdf',
    file_size: 409600,
    status: 'VERIFIED',
    checksum: 'jkl012mno345',
    uploaded_by: 2,
    uploaded_at: '2024-03-10T16:45:00Z'
  },
  {
    id: 5,
    applicant_id: 2,
    kind: 'FEE_RECEIPT',
    document_type: 'Payment Receipt',
    url: '/mock-documents/payment_receipt.pdf',
    file_name: 'license_fee_receipt_20240115.pdf',
    file_type: 'application/pdf',
    file_size: 102400,
    status: 'VERIFIED',
    checksum: 'mno345pqr678',
    uploaded_by: 4,
    uploaded_at: '2024-01-15T09:00:00Z'
  }
];

// Required documents for license application
export const requiredDocuments = [
  { kind: 'PHOTO', label: 'Passport Photograph', required: true },
  { kind: 'ID_DOC', label: 'Government ID (NIN Card)', required: true },
  { kind: 'MEDICAL_CERT', label: 'Medical Certificate', required: true },
  { kind: 'KNOWLEDGE_TEST', label: 'Knowledge Test Certificate', required: false },
  { kind: 'SKILL_TEST', label: 'Skill Test Certificate', required: true },
  { kind: 'FEE_RECEIPT', label: 'Payment Receipt', required: true },
] as const;

// Helper to get attachments for current user
export const getMyAttachments = (): MockAttachment[] => {
  return mockAttachments.filter(a => a.applicant_id === 1);
};

// Helper to check if a document type is uploaded
export const hasDocument = (kind: AttachmentKind): boolean => {
  return mockAttachments.some(a => a.applicant_id === 1 && a.kind === kind);
};

// Helper to get document upload progress
export const getDocumentProgress = (): { uploaded: number; total: number; percentage: number } => {
  const requiredCount = requiredDocuments.filter(d => d.required).length;
  const uploadedRequired = requiredDocuments
    .filter(d => d.required && hasDocument(d.kind as AttachmentKind))
    .length;
  
  return {
    uploaded: uploadedRequired,
    total: requiredCount,
    percentage: Math.round((uploadedRequired / requiredCount) * 100)
  };
};
